import { useState } from 'react';

import AccordionViewV3 from 'components/AccordionViewV3/AccordionViewV3';
import AlertViewV3 from 'components/AlertViewV3/AlertViewV3';
import ButtonViewV3 from 'components/ButtonViewV3/ButtonViewV3';
import TextViewV3 from 'components/TextViewV3/TextViewV3';

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const isDev = process.env.NODE_ENV === 'development';

const ErrorFallbackComponent = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

  const copyErrorToClipboard = async () => {
    const errorDetails = `Error: ${error.message}\nStack: ${
      error.stack || 'Not available'
    }\nTimestamp: ${new Date().toISOString()}\nURL: ${window.location.href}`;
    try {
      await navigator.clipboard.writeText(errorDetails);
      setCopyStatus('copied');

      // Reset status after 2.5 seconds
      setTimeout(() => {
        setCopyStatus('idle');
      }, 2500);
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  const errorDetailsAccordion = [
    {
      title: 'Error Details',
      content: (
        <div className="space-y-3 py-2">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <TextViewV3 variant="b4" color="#DC2626">
              <strong>Error:</strong> {error.message}
            </TextViewV3>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <TextViewV3 variant="b4" color="#6B7280">
              <strong>Time:</strong> {new Date().toLocaleString()}
            </TextViewV3>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <TextViewV3 variant="b4" color="#6B7280">
              <strong>Page:</strong> {window.location.pathname}
            </TextViewV3>
          </div>

          {error.stack && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-32 overflow-auto">
              <TextViewV3 variant="b4" color="#6B7280">
                <strong>Stack Trace:</strong>
              </TextViewV3>
              <pre className="text-xs mt-2 text-gray-600 whitespace-pre-wrap">
                {error.stack}
              </pre>
            </div>
          )}

          <ButtonViewV3
            onClick={copyErrorToClipboard}
            label={copyStatus === 'copied' ? 'Copied!' : 'Copy Error Details'}
            variant="tertiary"
            size="s"
            leadingIcon={copyStatus === 'copied' ? 'check' : 'copy-01'}
          />
        </div>
      ),
      open: false,
      disabled: false,
      showIndicator: false,
    },
  ];

  const handleRetry = async () => {
    setIsRetrying(true);
    setTimeout(() => {
      setIsRetrying(false);
      resetErrorBoundary();
    }, 1000);
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      role="alert"
    >
      <div className="max-w-3xl w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-red-400 to-red-500 px-8 py-6 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <TextViewV3 variant="h3" color="white">
                Oops! Something went wrong
              </TextViewV3>
              <TextViewV3 variant="b4" color="rgba(255,255,255,0.9)">
                We encountered an unexpected error
              </TextViewV3>
            </div>
          </div>
        </div>

        <div className="px-8 py-6">
          <div className="mb-6">
            <AlertViewV3
              type="error"
              title="Application Error"
              info="Don't worry! Our team has been automatically notified about this issue."
              showIcon={true}
              variant="floated-with-shadow"
            />
          </div>

          <div className="flex justify-end space-x-4 mb-6">
            <ButtonViewV3
              onClick={handleReload}
              label="Reload Page"
              variant="secondary"
              size="m"
              leadingIcon="refresh-cw-03"
            />
            <ButtonViewV3
              onClick={handleRetry}
              label={isRetrying ? 'Retrying...' : 'Try Again'}
              variant="primary"
              size="m"
              leadingIcon={isRetrying ? 'loader-01' : 'refresh-ccw-02'}
              disabled={isRetrying}
            />
          </div>

          {isDev && (
            <div className="border-t pt-4 mb-8">
              <AccordionViewV3 data={errorDetailsAccordion} />
            </div>
          )}

          <div className="mb-8 flex flex-col space-y-2">
            <TextViewV3
              variant="b4"
              children="Contact our support team if the issue persists."
            />
            <ButtonViewV3
              onClick={() => {
                const subject = 'Error Report';
                const body = `An error occurred.\nTimestamp: ${new Date().toISOString()}\nURL: ${
                  window.location.pathname
                }`;
                const mailtoLink = `mailto:help@yucollect.com?subject=${encodeURIComponent(
                  subject
                )}&body=${encodeURIComponent(body)}`;
                window.open(mailtoLink, '_blank');
              }}
              label="Email Support"
              variant="tertiary"
              size="s"
              leadingIcon="mail-01"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallbackComponent;
